import axios, {
  AxiosInstance,
  AxiosRequestConfig,
  CancelTokenSource,
} from "axios";
import { get, set } from "lodash-es";
import { GDT } from "@/types";
import { patchUrl } from "@/util/helpers";
import pinia, { useApi, useAuth, useOrgSettings } from "@/services";
import { BvModal } from "bootstrap-vue";

interface CancelableRequest {
  config: AxiosRequestConfig;
  source: CancelTokenSource;
}

const $http: AxiosInstance = axios.create();

const pendingRequests = {} as any;

const addBearer = (config: AxiosRequestConfig) => {
  const token = useAuth(pinia).accessToken;
  if (token) {
    const sources = Object.values(
      (useOrgSettings(pinia).settings?.mapSettings?.sources ||
        {}) as GDT.GammaDataSources
    )
      .filter((s) => s.type === "gammadata")
      .map((s) => patchUrl(s.dataRequest?.catalogUri.trim() || ""))
      .filter((s) => s);
    const apiUrls = sources.concat([
      useApi(pinia).baseDataUrl,
      useApi(pinia).baseUrl,
      useApi(pinia).riskFinderUrl,
      useApi(pinia).groupsUrl,
    ]);
    if (apiUrls.some((url) => (config.url as string).indexOf(url) > -1)) {
      set(config, "headers.Authorization", `Bearer ${token}`);
    }
  }
  return config;
};

let retried = false;

function setupAxios(bvModal: BvModal) {
  $http.interceptors.request.use(function (config) {
    //TEST Multitenant API
    //config.headers.RefererUrl =
    //  window.location.hostname === "localhost"
    //    ? process.env.VUE_APP_REFERER_URL
    //    : window.location.href;
    if (typeof config.url === "string") {
      config.url = patchUrl(config.url);
    }
    if (config.url) {
      addBearer(config);
    }
    return config;
  });

  $http.interceptors.response.use(
    (r) => {
      if (
        r.config.url &&
        r.config.url.indexOf(useApi(pinia).baseDataUrl) > -1
      ) {
        const calls = (pendingRequests[r.config.url] ||
          []) as CancelableRequest[];
        const callIndex = calls.findIndex(
          (c) =>
            JSON.parse(c.config.data)?.timestamp ===
            JSON.parse(r.config.data)?.timestamp
        );
        if (callIndex > -1) {
          calls
            .filter((c, i) => i < callIndex)
            .forEach((call) => call.source.cancel("canceled"));
          calls.splice(0, callIndex + 1);
          pendingRequests[r.config.url] = calls;
        }
      }
      return r;
    },
    function (err) {
      // show logout modal when UserPermissionsChangedException is recevied
      if (
        err.response.status === 403 &&
        err.response.data.exceptionType === "UserPermissionsChangedException"
      ) {
        bvModal.show("permission-changed");
      }

      if (get(err, "response.status") !== 401 || retried) {
        retried = false;
        return Promise.reject(err);
      }
      retried = true;
      return useAuth(pinia)
        .currentUserInfo()
        .then(() => $http.request(addBearer(err.config)))
        .catch((error) => console.log(error));
    }
  );
  return $http;
}

export function useAxios() {
  return { $http, setupAxios };
}
